import React from 'react';
import * as Sentry from '@sentry/react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { Button } from './ui';

import type { FallbackProps } from 'react-error-boundary';

interface QueryErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<FallbackProps>;
}

function isAuthError(error: unknown): boolean {
  if (!error) return false;
  const err = error as any;
  if (err?.code === 'UNAUTHORIZED') return true;
  if (err?.status === 401) return true;
  const msg: string = err?.message || err?.error || '';
  return msg.toLowerCase() === 'unauthorized';
}

function AuthErrorFallback({
  resetErrorBoundary,
}: Pick<FallbackProps, 'resetErrorBoundary'>) {
  return (
    <div className="min-h-200 flex flex-col items-center justify-center p-4">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Your session has expired
        </h2>
        <p className="text-gray-600 mb-4">
          Please sign in again to continue.
        </p>
        <Button
          onClick={resetErrorBoundary}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Sign in
        </Button>
      </div>
    </div>
  );
}

function DefaultErrorFallback({
  error,
  resetErrorBoundary,
}: FallbackProps) {
  if (isAuthError(error)) {
    return (
      <AuthErrorFallback
        resetErrorBoundary={resetErrorBoundary}
      />
    );
  }

  const traceId = (error as any)?.traceId;

  return (
    <div className="min-h-200 flex flex-col items-center justify-center p-4">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Something went wrong
        </h2>
        <p className="text-gray-600 mb-4">
          Failed to load data. Please try again.
        </p>
        {traceId && (
          <p className="text-xs text-gray-500 mb-4">
            Trace ID:{' '}
            <code className="bg-gray-100 px-2 py-1 rounded">
              {traceId}
            </code>
          </p>
        )}
        <Button
          onClick={resetErrorBoundary}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Try again
        </Button>
      </div>
    </div>
  );
}

export function QueryErrorBoundary({
  children,
  fallback: FallbackComponent = DefaultErrorFallback,
}: QueryErrorBoundaryProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          FallbackComponent={FallbackComponent}
          onError={(error: unknown, errorInfo: any) => {
            Sentry.captureException(error, {
              extra: {
                componentStack: errorInfo?.componentStack,
                isAuthError: isAuthError(error),
              },
            });
          }}
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
